import { useSortableTable } from '~/hooks/useSortableTable';
import { Pagination } from '~/components/Pagination/Pagination';
import { useAuthentication } from '~/hooks/useAuthentication';
import { usePersonListQuery, type UsePersonListQueryParams } from '~/pages/person/queries';
import { useFilterForm } from '~/hooks/useFilterForm';
import { PersonSerializerResponse } from '~/apis/ssf';
import { Table } from '~/components/Table/Table';
import { Badge } from '~/components/Badge/Badge';
import { querySchema } from '../query-schema';
import { useParams } from '~/hooks/useParams';
import { Link } from '~/components/Link/Link';
import { Fragment } from 'react/jsx-runtime';
import { idParamSchema } from '~/schemas';
import { formatDate } from '~/utils/date';

function Row({ lifter, filteredYear }: { lifter: PersonSerializerResponse; filteredYear?: number }) {
  const { can } = useAuthentication();

  return (
    <Table.Row key={lifter.id}>
      <Table.Col>
        <Link to={`/lifters/${lifter.id}`}>
          {lifter.first_name} {lifter.last_name}
        </Link>
      </Table.Col>

      {can('create-lifter-license', lifter.club.id) && filteredYear && (
        <Table.Col>
          <Badge label="Ja" variant="success" />
        </Table.Col>
      )}

      {can('create-lifter-license', lifter.club.id) && (
        <Table.Col>
          <Badge label={`${lifter.latest_license_year ?? '-'}`} variant={lifter.is_licensed ? 'success' : 'default'} />
        </Table.Col>
      )}

      {can('create-lifter-license', lifter.club.id) && (
        <Table.Col align="right">
          {!lifter.is_licensed && <Link to={`/lifters/${lifter.id}/licenses/create`}>Ansök om licens</Link>}
        </Table.Col>
      )}
    </Table.Row>
  );
}

export function LifterListTable() {
  const { values } = useFilterForm(querySchema.parse);
  const { register: registerSort, values: sortValues } = useSortableTable('first_name');
  const { id } = useParams(idParamSchema.parse);
  const { can } = useAuthentication();

  const { data, isLoading } = usePersonListQuery({
    valid_license_on: can('list-club-admin', id) ? values.valid_license_on : formatDate(new Date(), 'yyyy-mm-dd'),
    license_year: can('list-club-admin', id) ? values.license_year : undefined,
    search: values.search,
    page_size: values.pageSize,
    page: values.pageIndex,
    club: id,
    ordering: sortValues.ordering as NonNullable<UsePersonListQueryParams>['ordering'],
  });

  return (
    <Fragment>
      <Table bg="even">
        <Table.Head>
          <Table.Row>
            <Table.Col {...registerSort('first_name')} as="th">Namn</Table.Col>
            {can('create-lifter-license', id) && values.license_year && (
              <Table.Col as="th">Licens {values.license_year}</Table.Col>
            )}
            {can('create-lifter-license', id) && (
              <Table.Col {...registerSort('license_year')} as="th">Senaste licens</Table.Col>
            )}
            {can('create-lifter-license', id) && <Table.Col as="th" />}
          </Table.Row>
        </Table.Head>

        <Table.Body>
          {isLoading ? (
            <Table.Loading cols={values.license_year ? 4 : 3} />
          ) : !data?.results.length ? (
            <Table.Empty colSpan={1} text="Denna förening har för tillfället inga licensierade lyftare" />
          ) : (
            data.results.map(lifter => <Row lifter={lifter} filteredYear={values.license_year} key={lifter.id} />)
          )}
        </Table.Body>
      </Table>

      <Pagination count={data?.count} />
    </Fragment>
  );
}
